import { ApiError, type AccountQrActiveResult, type AccountQrIssueResult, type HqApi } from '@hq/api-sdk';
import { CopyOutlined, DownloadOutlined, ReloadOutlined, StopOutlined } from '@ant-design/icons';
import { formatDisplayTime } from '@hq/utils';
import { Alert, App, Button, Descriptions, Empty, Modal, Popconfirm, Space, Spin, Tag, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { downloadDataUrl, qrDataUrl, safeFileName } from './qr';

export interface AccountQrModalProps {
  hq: HqApi;
  open: boolean;
  /** 目标账户（sys_user.id）。 */
  userId: string;
  userName?: string | null;
  onClose: () => void;
}

type QrData = AccountQrIssueResult | AccountQrActiveResult;

/**
 * 账户身份二维码弹窗（院方「人员与角色」、运营端「用户权限」共用）。
 *
 * 语义：一张码 = 一个账户的「现场身份证明」，App 扫码后可确认物料送达/签收；
 * 默认 30 天有效，重新生成会立即吊销旧码（已打印的工牌随之失效）。
 * 打开时优先复用当前有效码，避免每次查看都换码。
 */
export default function AccountQrModal({ hq, open, userId, userName, onClose }: AccountQrModalProps) {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState<QrData | null>(null);
  const [image, setImage] = useState<string>('');

  const issue = useCallback(async () => {
    setBusy(true);
    try {
      const result = await hq.auth.accountQr.issue(userId);
      setData(result);
      message.success('身份码已生成（旧码自动作废）');
    } catch (err) {
      message.error(err instanceof ApiError ? err.friendlyMessage : '生成失败，请重试');
    } finally {
      setBusy(false);
    }
  }, [hq, userId, message]);

  // 打开时先取当前有效码：有就直接展示（工牌不必重打），没有则自动签发一张
  useEffect(() => {
    if (!open || !userId) return undefined;
    let alive = true;
    setLoading(true);
    setData(null);
    setImage('');
    hq.auth.accountQr
      .get(userId)
      .then(async (active) => {
        if (!alive) return;
        if (active?.code) {
          setData(active);
          return;
        }
        const result = await hq.auth.accountQr.issue(userId).catch(() => null);
        if (alive && result) setData(result);
      })
      .catch(() => undefined)
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [open, userId, hq]);

  const code = data?.code ?? '';
  useEffect(() => {
    if (!code) {
      setImage('');
      return undefined;
    }
    let alive = true;
    void qrDataUrl(code)
      .then((url) => {
        if (alive) setImage(url);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [code]);

  const revoke = async () => {
    setBusy(true);
    try {
      await hq.auth.accountQr.revoke(userId);
      setData(null);
      setImage('');
      message.success('身份码已吊销');
    } catch (err) {
      message.error(err instanceof ApiError ? err.friendlyMessage : '吊销失败，请重试');
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      message.success('身份码内容已复制');
    } catch {
      message.warning('浏览器不允许访问剪贴板，请手动选择文本复制');
    }
  };

  const download = () => {
    if (!image) return;
    downloadDataUrl(image, `${safeFileName(`账户身份码_${userName ?? userId}`)}.png`);
  };

  const expireText = formatDisplayTime(data?.expireAt);
  const expired = data?.expireAt ? new Date(data.expireAt).getTime() < Date.now() : false;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={460}
      title={`账户身份码 · ${userName ?? data?.realName ?? userId}`}
      destroyOnHidden
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="App 扫码确认送达与签收"
        description="二维码内含机构、科室、账户与有效期，默认 30 天。重新生成会立即作废旧码（已打印的工牌随之失效）。"
      />

      {loading || busy ? (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <Spin />
        </div>
      ) : data && code ? (
        <>
          <div style={{ textAlign: 'center', marginBottom: 12 }}>
            {image ? (
              <img
                src={image}
                alt="账户身份码"
                width={240}
                height={240}
                style={{ border: '1px solid #f0f0f0', borderRadius: 8, background: '#fff' }}
              />
            ) : (
              <Spin />
            )}
            <div style={{ marginTop: 8 }}>
              {expired ? <Tag color="red">已过期</Tag> : <Tag color="green">有效</Tag>}
              <Typography.Text type="secondary">有效期至 {expireText}</Typography.Text>
            </div>
          </div>

          <Descriptions column={1} size="small" styles={{ label: { width: 84 } }}>
            <Descriptions.Item label="账户">
              {data.realName ?? '-'}
              {data.userCode ? <Typography.Text type="secondary"> · {data.userCode}</Typography.Text> : null}
            </Descriptions.Item>
            <Descriptions.Item label="机构">{data.orgName ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="科室">{data.deptName ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="扫码次数">
              {'scanCount' in data ? (data.scanCount ?? 0) : 0}
            </Descriptions.Item>
          </Descriptions>

          <Space wrap style={{ marginTop: 16 }}>
            <Button type="primary" icon={<DownloadOutlined />} onClick={download}>
              下载图片
            </Button>
            <Button icon={<CopyOutlined />} onClick={() => void copyCode()}>
              复制内容
            </Button>
            <Popconfirm
              title="重新生成身份码？"
              description="旧码立即作废，已打印的工牌需要重新打印。"
              okText="重新生成"
              cancelText="取消"
              onConfirm={() => void issue()}
            >
              <Button icon={<ReloadOutlined />}>重新生成</Button>
            </Popconfirm>
            <Popconfirm
              title="吊销该身份码？"
              description="吊销后该账户需重新生成才能用于扫码签收。"
              okText="吊销"
              okButtonProps={{ danger: true }}
              cancelText="取消"
              onConfirm={() => void revoke()}
            >
              <Button danger icon={<StopOutlined />}>
                吊销
              </Button>
            </Popconfirm>
          </Space>
        </>
      ) : (
        <>
          <Empty description="该账户暂未生成身份码" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          <div style={{ textAlign: 'center' }}>
            <Button type="primary" loading={busy} onClick={() => void issue()}>
              生成身份码
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}
